import { ActivityIndicator, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { Text, View } from "@src/components";
import { isWeb } from "@src/utils/web-helpers";
import {
  RoadSenseReportNotFoundError,
  RoadSenseReportNotReadyError,
  useRoadSenseReport,
} from "@src/features/roadsense/hooks/useRoadSenseReport";
import { NO_REPORT_ACCESS_MESSAGE } from "@src/features/roadsense/hooks/roadsense-report-errors";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { useIsTenantAdmin } from "@visioni/a3s-access/app";
import { showsReportExportAction } from "@src/features/roadsense/site-report-actions";
import { ReportExportAction } from "@src/features/roadsense/components/ReportExportAction";
import { RoadSenseReportView } from "@src/features/roadsense/components/RoadSenseReportView";
import { useTrafficFiltersParams } from "@src/features/roadsense/hooks/useTrafficFiltersParams";
import type { RoadSensePayload } from "@visioni/a3s-reports/client-messages";
import { instantsDayBounds, spanDays, type TrafficFilters } from "@src/features/roadsense/filters/traffic-filters";

/**
 * RoadSense **report detail** at
 * `…/reports/roadsense/report/<reportId>/site/<siteId>`.
 *
 * `reportId` is the report row's ULID — the report's canonical identity (see
 * the identity note in `get-report.ts`). `siteId` selects which of the
 * lifecycle's sites to render. Both are opaque to this screen: passed straight
 * to the fetch hook, never parsed. RoadSense reports are cumulative from an
 * asset's hire start, so `periodStart` is not a unique key and must not be used
 * to identify a report.
 */
export default function RoadSenseReportDetailScreen() {
  const params = useLocalSearchParams<{ reportId?: string; siteId?: string }>();
  // Expo Router types a param as `string | string[]` (a segment can repeat).
  // A single path segment is always a string in practice, but narrowing here
  // keeps the fetch hook from silently seeing an array.
  const reportId = firstParam(params.reportId);
  const siteId = firstParam(params.siteId);

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          RoadSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this report in your browser to view it.
        </Text>
      </View>
    );
  }

  return <LiveReport reportId={reportId} siteId={siteId} />;
}

/**
 * Expo Router params are `string | string[]` (a segment can repeat). Path
 * segments never repeat in practice, but this collapses to the first value (or
 * `undefined`) so the rest of the screen only ever deals with a plain string.
 */
function firstParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

const SCROLL_CONTENT = {
  paddingTop: 20,
  paddingBottom: 20,
  paddingHorizontal: 20,
  width: "100%",
} as const;

function LiveReport({ reportId, siteId }: { reportId: string | undefined; siteId: string | undefined }) {
  // `workbookS3Key` rides along on the same live-payload response, so the
  // download button's visibility is tied to the report on screen — no second
  // fetch that could fail independently and hide the button.
  const { payload, workbookS3Key, workbookPeriodEnd, isLoading, isError, error, hasNoReportAccess, isTenantPending } =
    useRoadSenseReport(reportId, siteId);
  // The camera-scoped routes have no download, export or email signup yet
  // (DEL-1249 steps 6 and 7). With the flag on, a tenant admin keeps them on
  // the existing admin-only routes (the scoped live route sends admins the
  // workbook fields); everyone else gets none. See `showsReportExportAction`.
  const cameraScoped = useBooleanFlag(FEATURE_FLAGS.ROADSENSE_CAMERA_ACCESS);
  const { isTenantAdmin, isLoading: isAdminLoading } = useIsTenantAdmin();
  const showExportAction = showsReportExportAction({
    cameraScoped,
    // A failed admin check reads as false (non-admin).
    isTenantAdmin: isAdminLoading ? undefined : isTenantAdmin,
  });
  const { filters, setFilters } = useTrafficFiltersParams(
    payload ? spanDays(payload.trend.instants) : undefined,
    payload ? (instantsDayBounds(payload.trend.instants) ?? undefined) : undefined,
  );

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={SCROLL_CONTENT} showsVerticalScrollIndicator={false}>
      <Body
        reportId={reportId}
        siteId={siteId}
        payload={payload}
        workbookS3Key={workbookS3Key}
        workbookPeriodEnd={workbookPeriodEnd}
        showExportAction={showExportAction}
        isLoading={isLoading}
        isError={isError}
        error={error}
        hasNoReportAccess={hasNoReportAccess}
        isTenantPending={isTenantPending}
        filters={filters}
        onFiltersChange={setFilters}
      />
    </ScrollView>
  );
}

interface BodyProps {
  reportId: string | undefined;
  siteId: string | undefined;
  payload: RoadSensePayload | undefined;
  workbookS3Key: string | undefined;
  /** The last day the stored workbook covers, shown on the download button. */
  workbookPeriodEnd: string | undefined;
  /** Whether to offer the workbook download / Export menu. */
  showExportAction: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  /** The camera-scoped route answered 403. */
  hasNoReportAccess: boolean;
  isTenantPending: boolean;
  filters: TrafficFilters;
  onFiltersChange: (next: TrafficFilters) => void;
}

function Body({
  reportId,
  siteId,
  payload,
  workbookS3Key,
  workbookPeriodEnd,
  showExportAction,
  isLoading,
  isError,
  error,
  hasNoReportAccess,
  isTenantPending,
  filters,
  onFiltersChange,
}: BodyProps) {
  if (!reportId || !siteId) {
    return (
      <Message
        title="Invalid report"
        body="This URL is missing a site or report id. Pick a report from the list to continue."
      />
    );
  }

  if (isTenantPending) {
    return <Loading label="Resolving tenant…" />;
  }

  if (isLoading && !payload) {
    return <Loading label="Loading report…" />;
  }

  if (isError && error instanceof RoadSenseReportNotFoundError) {
    return (
      <Message
        title="Report not found"
        body="No RoadSense report exists for this id. It may have been deleted or never generated."
      />
    );
  }

  if (isError && hasNoReportAccess) {
    return <Message title={NO_REPORT_ACCESS_MESSAGE.title} body={NO_REPORT_ACCESS_MESSAGE.body} />;
  }

  if (isError && error instanceof RoadSenseReportNotReadyError) {
    const detail =
      error.status === "failed" ?
        "Report is unavailable at the moment. Please reload in a few minutes."
      : "Report is being generated. Please reload in a few minutes.";
    return <Message title="Report not ready" body={detail} />;
  }

  if (isError) {
    // Deliberately generic copy — the raw API error body (e.g. a 422 validation
    // JSON blob) is not user-facing. The actionable cases (not found / not ready)
    // are handled above with specific guidance.
    return (
      <Message
        title="Couldn't load report"
        body="Something went wrong loading this report. Please refresh the page, or pick another report from the list."
      />
    );
  }

  if (!payload) {
    // Decode failed (logged by the hook) — surface a generic message so the
    // screen never falls through to a blank container. We still offer the
    // workbook download (and report emails, behind `roadsense_export`): reaching here
    // means the live fetch *succeeded* (so `workbookS3Key` is the valid key
    // from the same response) and only the payload failed schema validation.
    // The `.xlsx` is a separate artefact that doesn't depend on the render
    // schema, so frontend payload drift must not cost the user their workbook
    // access.
    if (!showExportAction) {
      return (
        <Message
          title="Report unavailable"
          body="The report payload could not be decoded. The schema may be out of date — please reload the page."
        />
      );
    }
    return (
      <View gap="lg">
        <Message
          title="Report unavailable"
          body="The report payload could not be decoded. The schema may be out of date — please reload the page. You can still download the workbook below."
        />
        <ReportExportAction
          reportId={reportId}
          siteId={siteId}
          workbookS3Key={workbookS3Key}
          workbookPeriodEnd={workbookPeriodEnd}
        />
      </View>
    );
  }

  return (
    <RoadSenseReportView
      payload={payload}
      filters={filters}
      onFiltersChange={onFiltersChange}
      headerAction={
        showExportAction ?
          <ReportExportAction
            reportId={reportId}
            siteId={siteId}
            workbookS3Key={workbookS3Key}
            workbookPeriodEnd={workbookPeriodEnd}
          />
        : undefined
      }
    />
  );
}

interface MessageProps {
  title: string;
  body: string;
}

function Message({ title, body }: MessageProps) {
  return (
    <View padding="3xl" alignItems="center" gap="sm">
      <Text textStyle="sm/semibold" color="text" textAlign="center">
        {title}
      </Text>
      <Text textStyle="xs/regular" color="textMuted" textAlign="center">
        {body}
      </Text>
    </View>
  );
}

interface LoadingProps {
  label: string;
}

function Loading({ label }: LoadingProps) {
  return (
    <View padding="3xl" alignItems="center" gap="md">
      <ActivityIndicator />
      <Text textStyle="sm/regular" color="textMuted">
        {label}
      </Text>
    </View>
  );
}
